/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Client-side helpers that mirror the canonical admin-metadata contract from
 * `libs/dto/src/admin-metadata.dto.ts`.
 *
 * These are kept separate from the NestJS-only lib so the React frontend can
 * apply the same visibility/ownership rules for filtering, rendering guards,
 * and action affordances without a backend round-trip.
 *
 * @since Phase B — Issue #465B
 */

/** Visibility scope for a configurable object. */
export type ConfigObjectVisibility = 'public' | 'private';

/**
 * Lifecycle state — *has this been accepted into service?*
 *
 * `'draft'` awaits approval and is never selectable at runtime; absent means `'active'`.
 * Orthogonal to `enabled`, which asks whether the operator switched it on
 * (`docs/ARTIFACT_REVIEW_LIFECYCLE_DESIGN.md` L1).
 */
export type ConfigObjectStatus = 'draft' | 'active';

/** Minimum shape required for metadata helpers to operate. */
export interface HasAdminMetadata {
  ownerId?: string;
  /** Who manages it (`OT5`). Read through `resolveStewardship` (`@artifact-stewardship`), never directly. */
  managedBy?: string;
  visibility?: ConfigObjectVisibility;
  status?: ConfigObjectStatus;
  enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Access
// ---------------------------------------------------------------------------

// **Who may see and change an artifact is the server's answer** (`RQ11`, `#1510`): it stamps `canEdit` and
// `canDelete` on every read, and `@artifact-access` holds the one rule for anything with no stamp. The
// mirrors that lived here (`resolveVisibility`, `isOwnedBy`, `isVisibleTo`, `canMutate`,
// `filterByVisibility`) had no ad_ops rule and no visibility clamp, and were one of six that disagreed.

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * Mirrors `resolveStatus` in `libs/dto/src/admin-metadata.dto.ts`.
 *
 * An unrecognised stored value resolves to `'active'` — the safe direction for a field
 * arriving as JSON, since a typo must not quietly withdraw a live artifact from service.
 */
export function resolveStatus(obj: Pick<HasAdminMetadata, 'status'>): ConfigObjectStatus {
  return obj.status === 'draft' ? 'draft' : 'active';
}

/** Mirrors `isDraft` in `libs/dto/src/admin-metadata.dto.ts`. */
export function isDraft(obj: Pick<HasAdminMetadata, 'status'>): boolean {
  return resolveStatus(obj) === 'draft';
}

/**
 * Mirrors `isSelectable` in `libs/dto/src/admin-metadata.dto.ts`:
 * `status !== 'draft' && enabled !== false`.
 *
 * The lifecycle half of selectability: may this artifact be *used* — offered in a picker,
 * dispatched to, counted as available — independent of who is asking? Console lists are
 * already visibility-scoped by the API, so this is the half they need.
 *
 * **The one place a lifecycle state extends**, which is how the console's four pickers
 * picked up the draft rule without being edited again.
 *
 * Not for rendering an on/off toggle: that asks whether the operator switched it off, and
 * **a draft is not a disabled artifact**. Those sites keep reading `enabled` directly —
 * routing them through here would render every draft as switched off, and its approval
 * would then read as an operator switching it on.
 */
export function isSelectable(obj: Pick<HasAdminMetadata, 'enabled' | 'status'>): boolean {
  if (isDraft(obj)) return false;
  return obj.enabled !== false; // default true when absent
}
